import React from 'react';
import type { Course } from '../../types/course';
import type { CompletedLevelsMap } from '../../types/course';
import { FINAL_QUIZ_ID } from '../../config/courseContent';

interface Props {
  course: Course;
  completedLevels: CompletedLevelsMap;
  isLevelUnlocked: (levelId: string) => boolean;
  onSelectLevel: (levelId: string) => void;
  onSelectFinalQuiz: () => void;
  onLockedClick: (needsAuth: boolean) => void;
}

export const CourseOverview: React.FC<Props> = ({
  course,
  completedLevels,
  isLevelUnlocked,
  onSelectLevel,
  onSelectFinalQuiz,
  onLockedClick,
}) => {
  const allLevelIds = course.sections.flatMap((s) => s.levels.map((l) => l.id));
  const totalLevels = allLevelIds.length;
  const completedCount = allLevelIds.filter((id) => completedLevels.has(id)).length;
  const finalQuizCompleted = completedLevels.has(FINAL_QUIZ_ID);
  const finalQuizUnlocked = isLevelUnlocked(FINAL_QUIZ_ID);

  return (
    <div className="max-w-md mx-auto px-4 py-6">
      {/* Course header */}
      <div className="text-center mb-8">
        <h1 className="text-xl font-bold font-serif text-[var(--clue-text-primary)]">
          {course.title}
        </h1>
        <p className="text-sm text-[var(--clue-text-secondary)] mt-2 leading-relaxed">
          {course.description}
        </p>
        {/* Progress summary */}
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[var(--clue-bg-secondary)] px-4 py-1.5 text-xs font-medium text-[var(--clue-text-secondary)]">
          <span className="text-[var(--clue-green-accent)]">{completedCount}</span>
          <span>/</span>
          <span>{totalLevels} levels</span>
          {completedCount === totalLevels && (
            <span className="text-[var(--clue-green-accent)] ml-1">&#10003;</span>
          )}
        </div>
      </div>

      {/* Section + Level path */}
      {course.sections.map((section, sectionIdx) => (
        <div key={section.id} className="mb-8">
          {/* Section header */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">{section.icon}</span>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--clue-text-tertiary)]">
              {section.title}
            </h2>
          </div>

          {/* Level nodes */}
          <div className="flex flex-col items-center gap-0">
            {section.levels.map((level, levelIdx) => {
              const isCompleted = completedLevels.has(level.id);
              const isUnlocked = isLevelUnlocked(level.id);
              const isActive = isUnlocked && !isCompleted;
              const progress = completedLevels.get(level.id);
              const globalIndex = allLevelIds.indexOf(level.id);
              const isLastInSection = levelIdx === section.levels.length - 1;

              return (
                <React.Fragment key={level.id}>
                  <button
                    onClick={() => {
                      if (isUnlocked) {
                        onSelectLevel(level.id);
                      } else {
                        // Needs auth if it's level 2+ and guest
                        onLockedClick(globalIndex === 1);
                      }
                    }}
                    className={`relative w-full max-w-xs flex items-center gap-4 rounded-xl px-4 py-3 transition-all duration-200 text-left ${
                      isActive
                        ? 'bg-[var(--clue-green-accent)]/10 border border-[var(--clue-green-accent)]/40 shadow-sm'
                        : isCompleted
                          ? 'bg-[var(--clue-bg-secondary)] border border-[var(--clue-border-mid)]'
                          : 'bg-[var(--clue-bg-primary)] border border-[var(--clue-border-mid)] opacity-60'
                    }`}
                  >
                    {/* Node circle */}
                    <div
                      className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${
                        isCompleted
                          ? 'bg-[var(--clue-green-accent)] text-white'
                          : isActive
                            ? 'bg-[var(--clue-green-accent)]/20 text-[var(--clue-green-accent)] ring-2 ring-[var(--clue-green-accent)]'
                            : 'bg-[var(--clue-bg-secondary)] text-[var(--clue-text-tertiary)]'
                      }`}
                    >
                      {isCompleted ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        globalIndex + 1
                      )}
                    </div>

                    {/* Label */}
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-semibold truncate ${
                        isActive ? 'text-[var(--clue-text-primary)]' : 'text-[var(--clue-text-secondary)]'
                      }`}>
                        {level.title}
                      </p>
                      <p className="text-xs text-[var(--clue-text-tertiary)] truncate">
                        {isCompleted && progress
                          ? `${progress.quizScore}/${progress.quizTotal} correct`
                          : level.subtitle}
                      </p>
                    </div>

                    {/* Lock icon */}
                    {!isUnlocked && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--clue-text-tertiary)] shrink-0">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    )}
                  </button>

                  {/* Connector line */}
                  {!isLastInSection && (
                    <div className="w-0.5 h-4 bg-[var(--clue-border-mid)]" />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Connector between sections */}
          {sectionIdx < course.sections.length - 1 && (
            <div className="flex justify-center my-2">
              <div className="w-0.5 h-6 bg-[var(--clue-border-mid)]" />
            </div>
          )}
        </div>
      ))}

      {/* Final Quiz node */}
      <div className="flex flex-col items-center mt-4">
        <div className="w-0.5 h-4 bg-[var(--clue-border-mid)]" />
        <button
          onClick={() => {
            if (finalQuizUnlocked) {
              onSelectFinalQuiz();
            } else {
              onLockedClick(false);
            }
          }}
          className={`w-full max-w-xs flex items-center gap-4 rounded-xl px-4 py-3 transition-all duration-200 text-left ${
            finalQuizCompleted
              ? 'bg-[var(--clue-bg-secondary)] border border-[var(--clue-border-mid)]'
              : finalQuizUnlocked
                ? 'bg-[var(--clue-green-accent)]/10 border border-[var(--clue-green-accent)]/40 shadow-sm'
                : 'bg-[var(--clue-bg-primary)] border border-[var(--clue-border-mid)] opacity-60'
          }`}
        >
          <div
            className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center text-lg ${
              finalQuizCompleted
                ? 'bg-[var(--clue-green-accent)] text-white'
                : finalQuizUnlocked
                  ? 'bg-[var(--clue-green-accent)]/20 text-[var(--clue-green-accent)] ring-2 ring-[var(--clue-green-accent)]'
                  : 'bg-[var(--clue-bg-secondary)] text-[var(--clue-text-tertiary)]'
            }`}
          >
            {finalQuizCompleted ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              '🎓'
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-semibold ${
              finalQuizUnlocked ? 'text-[var(--clue-text-primary)]' : 'text-[var(--clue-text-secondary)]'
            }`}>
              Final Quiz
            </p>
            <p className="text-xs text-[var(--clue-text-tertiary)]">
              {finalQuizCompleted
                ? `${completedLevels.get(FINAL_QUIZ_ID)?.quizScore}/${completedLevels.get(FINAL_QUIZ_ID)?.quizTotal} correct`
                : '10 mixed questions from all levels'}
            </p>
          </div>
          {!finalQuizUnlocked && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--clue-text-tertiary)] shrink-0">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};
